import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { api, ApiError, type User } from "../api/client";
import { getDeviceToken } from "./device";

type AuthState = {
  user: User | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Restore an existing session on first load.
  useEffect(() => {
    let cancelled = false;
    api
      .me()
      .then((u) => {
        if (!cancelled) setUser(u);
      })
      .catch((err) => {
        if (!(err instanceof ApiError && err.status === 401)) {
          // Non-401 errors (network/server) are non-fatal for the shell.
          console.error(err);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Re-validate the session so a login elsewhere (single active session, §7.1)
  // logs THIS browser out promptly instead of only on the next reload: poll
  // periodically and whenever the tab/window regains focus. A 401 means our
  // session was superseded → drop the user, which redirects to /login.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const revalidate = () => {
      if (document.visibilityState === "hidden") return;
      api.me().catch((err) => {
        if (!cancelled && err instanceof ApiError && err.status === 401) setUser(null);
      });
    };
    const id = window.setInterval(revalidate, 30_000);
    window.addEventListener("focus", revalidate);
    document.addEventListener("visibilitychange", revalidate);
    return () => {
      cancelled = true;
      window.clearInterval(id);
      window.removeEventListener("focus", revalidate);
      document.removeEventListener("visibilitychange", revalidate);
    };
  }, [user]);

  const login = useCallback(async (username: string, password: string) => {
    const { user: u } = await api.login(username, password, getDeviceToken());
    setUser(u);
  }, []);

  const logout = useCallback(async () => {
    await api.logout();
    setUser(null);
  }, []);

  const value = useMemo(() => ({ user, loading, login, logout }), [user, loading, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
